import React, { useCallback, useEffect, useRef, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";

import { EmptyState, Section, StatCard, StatusBadge } from "../../components/ui";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getTokens } from "../../styles/tokens";

import { formatCentavos } from "./helpers";

const REFRESH_INTERVAL_MS = 60000;

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    updatedAt: {
      color: t.textSecondary,
      fontSize: 12,
      marginBottom: 14,
      fontVariantNumeric: "tabular-nums",
    },
    cards: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    },
    skeleton: { borderRadius: t.radius.md },
    breakdown: {
      marginTop: 16,
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
    },
  };
});

const humanizeKey = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());

const isPlainNumber = (v) => typeof v === "number" && Number.isFinite(v);

const KNOWN_TOP_KEYS = [
  "online",
  "leads",
  "pagamentos",
  "receita",
  "receitaCent",
  "onlinePorTipo",
  "onlineByType",
];

const Card = ({ label, value, tone = "brand" }) => <StatCard tone={tone} label={label} value={value} />;

const SummaryTab = () => {
  const classes = useStyles();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/hotspot/summary");
      if (!isMountedRef.current) return;
      setSummary(data && typeof data === "object" ? data : {});
      setUpdatedAt(new Date());
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    const timer = setInterval(() => {
      fetchSummary();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchSummary]);

  const data = summary || {};

  const onlineByType = data.onlinePorTipo || data.onlineByType;
  const leads = data.leads;
  const pagamentos = data.pagamentos;
  const receitaCents = data.receitaCent ?? data.receita;

  const genericCards = Object.entries(data).filter(
    ([key, value]) => !KNOWN_TOP_KEYS.includes(key) && isPlainNumber(value)
  );

  const hasAnyData =
    !loading &&
    (isPlainNumber(data.online) ||
      leads ||
      pagamentos ||
      receitaCents !== undefined ||
      genericCards.length > 0);

  const updatedLabel = updatedAt
    ? updatedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : "--:--";

  return (
    <>
      <div className={classes.updatedAt}>
        {i18n.t("hotspot.summary.updatedAt", { time: updatedLabel })}
      </div>

      {loading ? (
        <div className={classes.cards} aria-busy="true">
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} variant="rect" height={96} className={classes.skeleton} />
          ))}
        </div>
      ) : !hasAnyData ? (
        <Section>
          <EmptyState title={i18n.t("hotspot.summary.empty")} />
        </Section>
      ) : (
        <div className={classes.cards}>
          {isPlainNumber(data.online) && (
            <Card tone="success" label={i18n.t("hotspot.summary.cards.online")} value={data.online} />
          )}
          {leads && (
            <>
              <Card
                label={i18n.t("hotspot.summary.cards.leadsToday")}
                value={isPlainNumber(leads.hoje) ? leads.hoje : "—"}
              />
              <Card
                label={i18n.t("hotspot.summary.cards.leads7d")}
                value={isPlainNumber(leads["7d"]) ? leads["7d"] : "—"}
              />
              <Card
                label={i18n.t("hotspot.summary.cards.leads30d")}
                value={isPlainNumber(leads["30d"]) ? leads["30d"] : "—"}
              />
            </>
          )}
          {pagamentos && (
            <>
              <Card
                tone="success"
                label={i18n.t("hotspot.summary.cards.paymentsPaid")}
                value={isPlainNumber(pagamentos.pagos) ? pagamentos.pagos : "—"}
              />
              <Card
                tone="warning"
                label={i18n.t("hotspot.summary.cards.paymentsPending")}
                value={isPlainNumber(pagamentos.pendentes) ? pagamentos.pendentes : "—"}
              />
              <Card
                tone="error"
                label={i18n.t("hotspot.summary.cards.paymentsExpired")}
                value={isPlainNumber(pagamentos.expirados) ? pagamentos.expirados : "—"}
              />
            </>
          )}
          {receitaCents !== undefined && (
            <Card
              label={i18n.t("hotspot.summary.cards.revenue")}
              value={isPlainNumber(receitaCents) ? formatCentavos(receitaCents) : "—"}
            />
          )}
          {genericCards.map(([key, value]) => (
            <Card key={key} tone="neutral" label={humanizeKey(key)} value={value} />
          ))}
        </div>
      )}

      {onlineByType && typeof onlineByType === "object" && (
        <div className={classes.breakdown}>
          {Object.entries(onlineByType).map(([type, count]) => (
            <StatusBadge
              key={type}
              color="neutral"
              dot={false}
              label={`${humanizeKey(type)}: ${isPlainNumber(count) ? count : "—"}`}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default SummaryTab;
